import { ThemeEvents } from '@theme/events';

/**
 * Shows a merchant-configured one-liner based on how many units of this
 * product are already in the cart. Updates on cart:update.
 */
class CartQuantityMessage extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Record<string, string>} */
  #messages = {};

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#messages = this.#parseMessages();
    this.#render(this.#initialQuantity());

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @returns {Record<string, string>}
   */
  #parseMessages() {
    const script = this.querySelector('[data-cart-quantity-messages]');
    if (!(script instanceof HTMLScriptElement)) return {};

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @returns {number}
   */
  #initialQuantity() {
    const value = Number.parseInt(this.dataset.cartQuantity || '0', 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const cart = detail?.resource;

    if (cart?.items) {
      this.#render(this.#quantityFromCart(cart));
      return;
    }

    this.#fetchCartQuantity().then((qty) => this.#render(qty));
  };

  /**
   * @param {{ items?: Array<{ product_id?: number|string, quantity?: number }> }} cart
   * @returns {number}
   */
  #quantityFromCart(cart) {
    const productId = this.dataset.productId;
    if (!productId || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      if (String(item.product_id) !== String(productId)) return total;
      return total + (Number(item.quantity) || 0);
    }, 0);
  }

  /**
   * @returns {Promise<number>}
   */
  async #fetchCartQuantity() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return 0;
      const cart = await response.json();
      return this.#quantityFromCart(cart);
    } catch {
      return 0;
    }
  }

  /**
   * @param {number} quantity
   */
  #render(quantity) {
    const safeQty = Math.max(0, Math.floor(quantity));
    const key = String(Math.min(safeQty, 5));
    const message = (this.#messages[key] || '').trim();
    const textEl = this.querySelector('[data-cart-quantity-message-text]');

    this.dataset.cartQuantity = String(safeQty);
    this.hidden = message.length === 0;

    if (textEl) {
      textEl.textContent = message;
    }
  }
}

if (!customElements.get('cart-quantity-message')) {
  customElements.define('cart-quantity-message', CartQuantityMessage);
}
